/**
 * Payment and credit system types
 */

export interface PaymentProvider {
  id: number;
  name: string;
  code: string;
  created_at: string;
  updated_at: string;
}

export interface CreditPackage {
  id: number;
  name: string;
  description: string | null;
  credits_amount: number;
  created_at: string;
  updated_at: string;
  variants: CreditPackageVariant[];
}

export interface CreditPackageVariant {
  id: number;
  package_id: number;
  provider_id: number;
  external_product_id: string;
  external_variant_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentCustomer {
  id: number;
  workspace_id: string;
  provider_id: number;
  external_customer_id: string;
  created_at: string;
  updated_at: string;
}

export interface PaymentOrder {
  id: string;
  workspace_id: string;
  variant_id: number;
  external_order_id: string;
  status: 'pending' | 'paid' | 'failed' | 'refunded' | 'cancelled';
  paid_at: string | null;
  cancelled_at: string | null;
  failed_at: string | null;
  refunded_at: string | null;
  created_at: string;
  updated_at: string;
}

export type CreditTransactionType = 'purchase' | 'usage' | 'refund' | 'bonus' | 'adjustment';

export interface CreditTransaction {
  id: string;
  workspace_id: string;
  transaction_type: CreditTransactionType;
  credits_change: number;
  transaction_metadata: Record<string, any> | null;
  payment_order_id: string | null;
  created_at: string;
}

// Request/Response types for API
export interface CheckoutRequest {
  variant_id: number;
  customer_email?: string;
  success_url?: string;
  cancel_url?: string;
}

export interface CheckoutResponse {
  checkout_url: string;
}

export interface CreditBalanceResponse {
  workspace_id: string;
  credits: number;
  last_updated: string;
}

export interface TransactionResponse {
  id: string;
  credits_change: number;
  balance_after: number;
  transaction_type: string;
  transaction_metadata: Record<string, any> | null;
  created_at: string;
}

export interface TransactionListResponse {
  transactions: CreditTransaction[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface CreditCostConfig {
  cv_analysis: number;
  jd_processing: number;
  ai_chat_message: number;
  cv_conversion: number;
  compatibility_analysis: number;
}

// Utility types for frontend
export interface PricingCardProps {
  variant: CreditPackageVariant;
  isPopular?: boolean;
  onSelect: (variantId: number) => void;
  isLoading?: boolean;
}

export interface TransactionHistoryFilters {
  type?: CreditTransactionType;
  start_date?: string;
  end_date?: string;
}

// Credit feature costs mapping - matches backend configuration
export const CREDIT_COSTS: Record<string, number> = {
  cv_analysis: 5, // CV-JD compatibility analysis
  jd_processing: 3, // Job description AI processing
  ai_chat_message: 1, // Agent chat messages
  cv_upload: 3, // CV upload and processing
  cv_conversion: 2, // CV conversion to JSON Resume format
  compatibility_analysis: 8, // CV-JD compatibility analysis
} as const;

// Transaction type display mapping
export const TRANSACTION_TYPE_LABELS: Record<CreditTransactionType, string> = {
  purchase: 'Credit Purchase',
  usage: 'Usage',
  refund: 'Refund',
  bonus: 'Bonus Credits',
  adjustment: 'Adjustment',
} as const;

// Transaction type icons (Lucide React icon names)
export const TRANSACTION_TYPE_ICONS: Record<CreditTransactionType, string> = {
  purchase: 'CreditCard',
  usage: 'Minus',
  refund: 'RotateCcw',
  bonus: 'Gift',
  adjustment: 'Settings',
} as const;
